import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { de, enUS, es, ru } from 'date-fns/locale';
import { format, addDays, startOfDay, parseISO } from 'date-fns';
import { useLanguage } from '../contexts/LanguageContext';
import { useForm } from '../contexts/FormContext';
import { executeRecaptcha } from '../utils/recaptcha';
import {
  getAvailabilityRange, getAvailabilityDay,
  createHold, releaseHold,
  AvailabilityDay, AvailabilitySlot,
} from '../services/bookingPublicApi';
import Button from './ui/Button';
import Card from './ui/Card';

// Public API base. Admin backend serves procedures with CORS allowing seadent.es.
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://admin.seadent.es/api'
  : 'http://localhost:5000/api';

const PROCEDURES_URL = `${API_BASE_URL}/procedures?bookableOnly=true`;
const BOOKING_URL    = `${API_BASE_URL}/public/booking`;

const LOOKAHEAD_DAYS = 60;
const DATE_FNS_LOCALE: Record<string, typeof de> = { de, en: enUS as typeof de, es, ru };
const fmtDate = (d: Date): string => format(d, 'yyyy-MM-dd');

interface ApiProcedure {
  id: number;
  key?: string;
  name: Record<string, string> | string;
  duration: number;
  is_active: boolean;
  bookable?: boolean;
}

const resolveName = (n: ApiProcedure['name'], locale: string): string => {
  if (!n) return '';
  if (typeof n === 'string') return n;
  return n[locale] || n.de || n.en || n.es || n.ru || Object.values(n).find(Boolean) || '';
};

const BookingForm: React.FC = () => {
  const { translations, language } = useLanguage();
  const { formContainerRef, focusAndCenterForm } = useForm();
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    treatment: '',
    insurance: '',
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [apiProcedures, setApiProcedures] = useState<ApiProcedure[]>([]);

  // --- availability + hold state ----------------------------------------
  const [availabilityDays, setAvailabilityDays] = useState<AvailabilityDay[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [daySlots, setDaySlots] = useState<AvailabilitySlot[]>([]);
  const [daySlotsLoading, setDaySlotsLoading] = useState(false);
  const [holdToken, setHoldToken] = useState<string | null>(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState<number | null>(null);
  const [holdRemaining, setHoldRemaining] = useState<number>(0);
  const [holdError, setHoldError] = useState<string | null>(null);
  const heldSlotRef = useRef<{ date: string; time: string; end_time: string } | null>(null);

  // Fetch live procedures from admin backend; falls back to i18n strings if it fails.
  useEffect(() => {
    fetch(PROCEDURES_URL)
      .then(r => r.json())
      .then(j => {
        if (j?.success && Array.isArray(j.data)) {
          setApiProcedures(j.data.filter((p: ApiProcedure) => p.is_active && p.bookable !== false));
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const treatment = params.get('treatment');
    if (treatment) {
      setFormData(prev => ({ ...prev, treatment }));
    }
    // As per user request: Always start at Step 1, even if treatment is pre-selected
    setStep(1);

    // GTM: Wizard gestartet
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_start',
        wizard_name: 'booking_form',
        language: language
      });
    }
  }, [location.search, language]);

  useEffect(() => {
    focusAndCenterForm();

    // GTM: Step View
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_step_view',
        wizard_name: 'booking_form',
        wizard_step: step,
        language: language
      });
    }
  }, [focusAndCenterForm, step, language]);

  // Treatments come from the admin backend (multilingual), with i18n strings as a fallback.
  const fallback = [
    { id: 'cleaning', label: translations.bookingWizard.treatments.cleaning },
    { id: 'returningCleaning', label: translations.bookingWizard.treatments.returningCleaning },
    { id: 'consultation', label: translations.bookingWizard.treatments.consultation },
    { id: 'cosmetic', label: translations.bookingWizard.treatments.cosmetic },
    { id: 'secondOpinion', label: translations.bookingWizard.treatments.secondOpinion },
    { id: 'emergency', label: translations.bookingWizard.treatments.emergency },
    { id: 'whitening', label: translations.bookingWizard.treatments.whitening },
    { id: 'botox', label: translations.bookingWizard.treatments.botox },
    { id: 'tmj', label: translations.bookingWizard.treatments.tmj },
    { id: 'pediatric', label: translations.bookingWizard.treatments.pediatric },
  ];
  const treatments = apiProcedures.length > 0
    ? apiProcedures.map(p => ({ id: p.key || String(p.id), label: resolveName(p.name, language) }))
    : fallback;

  // The procedure currently selected by the user, resolved against the live API list.
  const selectedProcedure = useMemo<ApiProcedure | null>(() => {
    if (!formData.treatment) return null;
    return apiProcedures.find(p =>
      p.key === formData.treatment || String(p.id) === formData.treatment
    ) || null;
  }, [apiProcedures, formData.treatment]);

  const procedureRef = useMemo(() => {
    if (!selectedProcedure) return null;
    return { procedureId: selectedProcedure.id, procedureKey: selectedProcedure.key };
  }, [selectedProcedure]);

  // Reset slot state whenever the user changes treatment.
  useEffect(() => {
    heldSlotRef.current = null;
    setSelectedDate(null);
    setDaySlots([]);
    setHoldToken(null);
    setHoldExpiresAt(null);
    setHoldError(null);
    setAvailabilityDays([]);
  }, [formData.treatment]);

  // Load range availability on entry to step 3 (or whenever procedure changes).
  useEffect(() => {
    if (step !== 3 || !procedureRef) return;
    let cancelled = false;
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    const today = startOfDay(new Date());
    const from = fmtDate(today);
    const to = fmtDate(addDays(today, LOOKAHEAD_DAYS));
    getAvailabilityRange({ ...procedureRef, from, to })
      .then(days => { if (!cancelled) setAvailabilityDays(days); })
      .catch(err => {
        if (!cancelled) setAvailabilityError(err?.message || 'Verfügbarkeit konnte nicht geladen werden');
      })
      .finally(() => { if (!cancelled) setAvailabilityLoading(false); });
    return () => { cancelled = true; };
  }, [step, procedureRef]);

  // Load slots when a day is picked.
  const loadDaySlots = useCallback(async (date: Date) => {
    if (!procedureRef) return;
    setDaySlotsLoading(true);
    try {
      const slots = await getAvailabilityDay({ ...procedureRef, date: fmtDate(date) });
      setDaySlots(slots);
    } catch {
      setDaySlots([]);
    } finally {
      setDaySlotsLoading(false);
    }
  }, [procedureRef]);

  // Hold countdown (1s tick). When it hits 0 we drop the hold and bounce the user
  // back to the slot picker — no booking can survive an expired reservation.
  useEffect(() => {
    if (!holdExpiresAt) { setHoldRemaining(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.round((holdExpiresAt - Date.now()) / 1000));
      setHoldRemaining(left);
      if (left <= 0) {
        setHoldToken(null);
        setHoldExpiresAt(null);
        setHoldError('Reservierung abgelaufen — bitte wähle den Termin erneut.');
        heldSlotRef.current = null;
        if (selectedDate) loadDaySlots(selectedDate);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [holdExpiresAt, selectedDate, loadDaySlots]);

  // Best-effort release on unmount.
  useEffect(() => {
    return () => {
      if (holdToken) releaseHold(holdToken);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop any active hold when the user navigates away from step 3.
  useEffect(() => {
    if (step !== 3 && holdToken) {
      releaseHold(holdToken);
      setHoldToken(null);
      setHoldExpiresAt(null);
      heldSlotRef.current = null;
    }
  }, [step, holdToken]);

  // Map day → hasSlots for fast lookup by the calendar.
  const availabilityByDate = useMemo(() => {
    const m = new Map<string, AvailabilityDay>();
    availabilityDays.forEach(d => m.set(d.date, d));
    return m;
  }, [availabilityDays]);

  const isDayDisabled = useCallback((d: Date): boolean => {
    const key = fmtDate(d);
    const entry = availabilityByDate.get(key);
    return !entry || !entry.hasSlots;
  }, [availabilityByDate]);

  const calendarRange = useMemo(() => ({
    from: startOfDay(new Date()),
    to: addDays(startOfDay(new Date()), LOOKAHEAD_DAYS),
  }), []);

  const handleDayPick = useCallback(async (date: Date | undefined) => {
    if (!date) return;
    // If we had a hold for a different slot, release it first.
    if (holdToken) {
      releaseHold(holdToken);
      setHoldToken(null);
      setHoldExpiresAt(null);
      heldSlotRef.current = null;
    }
    setHoldError(null);
    setSelectedDate(date);
    setFormData(prev => ({ ...prev, date: fmtDate(date), time: '' }));
    await loadDaySlots(date);
  }, [holdToken, loadDaySlots]);

  const handleSlotPick = useCallback(async (slot: AvailabilitySlot) => {
    if (!selectedDate || !procedureRef) return;
    setHoldError(null);
    // Release a previous hold (e.g. user changes their mind to another slot on the same day).
    if (holdToken) {
      releaseHold(holdToken);
      setHoldToken(null);
      setHoldExpiresAt(null);
    }
    try {
      const dateStr = fmtDate(selectedDate);
      const resp = await createHold({ ...procedureRef, date: dateStr, time: slot.start_time });
      setHoldToken(resp.holdToken);
      setHoldExpiresAt(Date.parse(resp.expiresAt));
      heldSlotRef.current = { date: dateStr, time: slot.start_time, end_time: resp.end_time };
      setFormData(prev => ({ ...prev, date: dateStr, time: slot.start_time }));
    } catch (err: any) {
      setHoldError(err?.status === 409
        ? 'Dieser Slot wurde gerade vergeben — bitte wähle einen anderen.'
        : 'Reservierung fehlgeschlagen. Bitte erneut versuchen.');
      // Refresh slot list since the world moved on.
      if (selectedDate) loadDaySlots(selectedDate);
    }
  }, [selectedDate, procedureRef, holdToken, loadDaySlots]);

  const handleReleaseAndPickAgain = useCallback(() => {
    if (holdToken) releaseHold(holdToken);
    setHoldToken(null);
    setHoldExpiresAt(null);
    heldSlotRef.current = null;
    setHoldError(null);
    if (selectedDate) loadDaySlots(selectedDate);
  }, [holdToken, selectedDate, loadDaySlots]);

  const handleTreatmentClick = (id: string) => {
    setFormData({ ...formData, treatment: id });
    setStep(2);

    // GTM: Treatment ausgewählt
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_treatment_selected',
        treatment_id: id,
        wizard_name: 'booking_form'
      });
    }
  };

  const handleInsuranceSelect = (insurance: string) => {
    setFormData({ ...formData, insurance });
    setStep(3);

    // GTM: Versicherung ausgewählt
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'wizard_insurance_selected',
        insurance_type: insurance,
        wizard_name: 'booking_form'
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: false });
    }
  };

  const validateStep3 = () => {
    const errors: Record<string, boolean> = {
      name: formData.name.trim() === '',
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
      phone: formData.phone.trim() === '',
      date: formData.date === '' || !holdToken,
      time: formData.time === '' || !holdToken
    };
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setIsSubmitting(true);
    setSubmitError(false);

    // Get the actual label for the selected treatment ID
    const selectedTreatment = treatments.find(t => t.id === formData.treatment || t.id.includes(formData.treatment));
    const treatmentLabel = selectedTreatment ? selectedTreatment.label : formData.treatment;

    // Resolve a numeric procedure ID for the admin backend. formData.treatment may be
    // either a key (e.g. "cleaning") or a numeric ID (string) when API procedures load.
    const matchingApi = apiProcedures.find(p => p.key === formData.treatment || String(p.id) === formData.treatment);
    const procedureId = matchingApi?.id;

    try {
      // reCAPTCHA token for the email endpoint (booking PHP requires it).
      let recaptchaToken = '';
      try {
        recaptchaToken = await executeRecaptcha('booking');
      } catch {
        // If reCAPTCHA fails to load, the PHP endpoint will reject and we
        // still try the admin-backend booking below.
      }

      // 1) Email to practice (existing PHP endpoint on seadent.es)
      const emailResp = await fetch('/api/email/booking/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({
          ...formData,
          treatment: treatmentLabel,
          treatmentId: formData.treatment,
          language,
          recaptchaToken,
        }),
      });
      const emailData = await emailResp.json().catch(() => ({}));

      // 2) Create pending appointment in admin backend (cross-origin to admin.seadent.es)
      let bookingOk = false;
      let bookingConflict = false;
      try {
        const bookingResp = await fetch(BOOKING_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            date: formData.date,
            time: formData.time,
            procedureId,                       // numeric, preferred
            treatment: formData.treatment,     // fallback (key)
            language,
            insurance: formData.insurance,
            message: formData.message,
            holdToken,                         // Phase 2: server validates & consumes
          }),
        });
        const bookingData = await bookingResp.json().catch(() => ({}));
        bookingOk = !!bookingData.success;
        if (bookingResp.status === 409) bookingConflict = true;
      } catch {
        // Admin backend unreachable — email already went out, treat submit as success.
        bookingOk = false;
      }

      if (bookingConflict) {
        // Hold expired or got out of sync. Drop local state and bounce back to the calendar.
        setHoldToken(null);
        setHoldExpiresAt(null);
        heldSlotRef.current = null;
        setHoldError('Dieser Termin ist in der Zwischenzeit vergeben worden. Bitte wähle einen neuen.');
        if (selectedDate) loadDaySlots(selectedDate);
        return;
      }

      if (emailData?.success || bookingOk) {
        setSubmitSuccess(true);

        // GTM: Wizard erfolgreich abgeschlossen
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'wizard_complete',
            wizard_name: 'booking_form',
            treatment_id: formData.treatment,
            insurance_type: formData.insurance,
            treatment_label: treatmentLabel,
            email_ok: !!emailData?.success,
            booking_ok: bookingOk,
          });
        }
      } else {
        setSubmitError(true);
      }
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <Card
      noPadding
      className="max-w-3xl mx-auto mt-12 mb-24 min-h-[650px] flex flex-col shadow-premium overflow-hidden border-0"
    >
      <div id="booking-form" ref={formContainerRef} className="flex-grow flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white sticky top-0 z-20">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className="flex items-center text-gray-500 hover:text-navy transition-colors font-bold text-sm"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
            {translations.bookingWizard.navigation.back}
          </button>
          <div className="flex flex-col items-center">
            <span className="font-bold text-navy tracking-tight text-lg">SeaDent Mallorca</span>
            <div className="flex space-x-1 mt-1">
              <div className={`h-1 w-8 rounded-full transition-colors ${step >= 1 ? 'bg-mint' : 'bg-gray-200'}`}></div>
              <div className={`h-1 w-8 rounded-full transition-colors ${step >= 2 ? 'bg-mint' : 'bg-gray-200'}`}></div>
              <div className={`h-1 w-8 rounded-full transition-colors ${step >= 3 ? 'bg-mint' : 'bg-gray-200'}`}></div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-navy transition-colors p-1"
            title={translations.bookingWizard.navigation.close}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-grow p-6 md:p-10 bg-white overflow-y-auto">
          {submitSuccess ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-8 animate-fadeIn">
              <div className="w-24 h-24 bg-mint/10 rounded-full flex items-center justify-center text-mint shadow-inner">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="space-y-4 max-w-md">
                <h4 className="text-3xl font-bold text-navy font-title">{translations.booking.form.success.split('!')[0]}!</h4>
                <p className="text-gray-500 text-lg leading-relaxed">{translations.booking.form.success.split('!')[1] || ''}</p>
              </div>
              <Button onClick={handleClose} variant="primary" size="lg" className="px-12 shadow-lg">OK</Button>
            </div>
          ) : (
            <div className="max-w-xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-navy mb-8 text-center font-title animate-slideIn">
                {step === 1 && translations.bookingWizard.steps.treatment}
                {step === 2 && translations.bookingWizard.steps.insurance}
                {step === 3 && translations.bookingWizard.steps.dateTime}
              </h2>

              {/* Step 1: Treatment Selection */}
              {step === 1 && (
                <div className="space-y-3 animate-fadeIn">
                  {treatments.map((t, index) => (
                    <button
                      key={t.id}
                      onClick={() => handleTreatmentClick(t.id)}
                      className={`w-full flex items-center justify-between p-5 border rounded-xl transition-all text-left group hover:shadow-md ${formData.treatment === t.id
                          ? 'border-mint bg-mint/5 ring-1 ring-mint'
                          : 'border-gray-100 bg-white hover:border-mint/50'
                        }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <span className={`font-bold text-lg group-hover:text-navy transition-colors ${formData.treatment === t.id ? 'text-navy' : 'text-gray-600'}`}>
                        {t.label}
                      </span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${formData.treatment === t.id ? 'bg-mint text-white' : 'bg-gray-50 text-gray-300 group-hover:text-mint'}`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2: Insurance Selection */}
              {step === 2 && (
                <div className="space-y-8 py-4 animate-fadeIn">
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                      <svg className="h-6 w-6 text-gray-400 group-focus-within:text-mint transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder={translations.bookingWizard.insurance.search}
                      className="block w-full pl-14 pr-4 py-5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-mint focus:border-mint shadow-sm outline-none transition-all text-lg"
                      autoFocus
                      onChange={(e) => setFormData({ ...formData, insurance: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && formData.insurance.trim()) {
                          setStep(3);
                        }
                      }}
                    />
                  </div>
                  <div className="flex flex-col items-center space-y-4">
                    <Button
                      onClick={() => {
                        setStep(3);
                        if (window.dataLayer) {
                          window.dataLayer.push({
                            event: 'wizard_insurance_selected',
                            insurance_type: formData.insurance,
                            wizard_name: 'booking_form'
                          });
                        }
                      }}
                      disabled={!formData.insurance.trim()}
                      variant="primary"
                      fullWidth
                      size="lg"
                      className="py-4 text-lg"
                    >
                      {translations.bookingWizard.navigation.next}
                    </Button>
                    <button
                      onClick={() => handleInsuranceSelect('none')}
                      className="text-gray-400 font-bold hover:text-navy hover:underline transition-colors text-sm uppercase tracking-wide"
                    >
                      {translations.bookingWizard.insurance.none}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: pick a slot, hold it, then collect patient data */}
              {step === 3 && (
                <div className="animate-fadeIn space-y-6">
                  {!holdToken ? (
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                          {translations.common.date}
                        </label>
                        {availabilityLoading && availabilityDays.length === 0 ? (
                          <div className="text-sm text-gray-500 py-6 text-center">
                            Verfügbarkeit wird geladen…
                          </div>
                        ) : availabilityError ? (
                          <div className="text-sm text-coral py-3">{availabilityError}</div>
                        ) : (
                          <div className="rdp-wrapper flex justify-center">
                            <DayPicker
                              mode="single"
                              required={false}
                              selected={selectedDate || undefined}
                              onSelect={handleDayPick}
                              startMonth={calendarRange.from}
                              endMonth={calendarRange.to}
                              disabled={[{ before: calendarRange.from }, { after: calendarRange.to }, isDayDisabled]}
                              locale={(DATE_FNS_LOCALE[language] || de) as any}
                              showOutsideDays={false}
                              modifiersClassNames={{
                                selected: 'bg-mint text-white',
                                disabled: 'text-gray-300',
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {selectedDate && (
                        <div className="bg-white p-4 rounded-xl border border-gray-100">
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                            {translations.common.time} — {format(selectedDate, 'PPP', { locale: DATE_FNS_LOCALE[language] || de })}
                          </label>
                          {daySlotsLoading ? (
                            <div className="text-sm text-gray-500 py-3">Slots werden geladen…</div>
                          ) : daySlots.length === 0 ? (
                            <div className="text-sm text-gray-500 py-3">Keine freien Termine an diesem Tag.</div>
                          ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {daySlots.map(s => (
                                <button
                                  key={s.start_time}
                                  type="button"
                                  onClick={() => handleSlotPick(s)}
                                  className="px-3 py-2 border border-gray-200 rounded-lg hover:border-mint hover:bg-mint/5 text-base font-medium text-navy transition-colors"
                                >
                                  {s.start_time}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {holdError && (
                        <div className="p-3 bg-coral/10 text-coral rounded-lg text-sm font-medium border border-coral/20">
                          {holdError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="bg-mint/5 border border-mint/30 rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <div className="text-xs font-bold text-mint uppercase tracking-wider">Reserviert</div>
                          <div className="text-navy font-semibold">
                            {heldSlotRef.current && format(parseISO(heldSlotRef.current.date), 'PPP', { locale: DATE_FNS_LOCALE[language] || de })}
                            {' · '}{heldSlotRef.current?.time}–{heldSlotRef.current?.end_time}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Verbleibend: {Math.floor(holdRemaining / 60)}:{String(holdRemaining % 60).padStart(2, '0')} min
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleReleaseAndPickAgain}
                          className="text-sm text-gray-500 hover:text-coral underline"
                        >
                          Anderen Termin wählen
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                          <input
                            type="text"
                            name="name"
                            placeholder={translations.booking.form.name}
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-mint outline-none text-base transition-all ${formErrors.name ? 'border-coral bg-coral/5' : 'border-gray-200'}`}
                          />
                          <input
                            type="email"
                            name="email"
                            placeholder={translations.booking.form.email}
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-mint outline-none text-base transition-all ${formErrors.email ? 'border-coral bg-coral/5' : 'border-gray-200'}`}
                          />
                          <input
                            type="tel"
                            name="phone"
                            placeholder={translations.booking.form.phone}
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full p-4 border rounded-lg focus:ring-2 focus:ring-mint outline-none text-base transition-all ${formErrors.phone ? 'border-coral bg-coral/5' : 'border-gray-200'}`}
                          />
                        </div>
                        <textarea
                          name="message"
                          placeholder={translations.booking.form.message}
                          value={formData.message}
                          onChange={handleChange}
                          rows={3}
                          className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-mint outline-none resize-none text-base transition-all"
                        ></textarea>
                      </div>

                      {submitError && (
                        <div className="p-4 bg-coral/10 text-coral rounded-lg text-sm font-bold border border-coral/20 animate-shake flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {translations.booking.form.error}
                        </div>
                      )}

                      <Button
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                        variant="primary"
                        fullWidth
                        size="lg"
                        className="text-lg py-4 shadow-lg"
                      >
                        {isSubmitting ? translations.booking.form.submitting || translations.booking.form.submit : translations.booking.form.submit}
                      </Button>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Branding Footer */}
      <div className="bg-gray-50 border-t border-gray-100 py-6 text-center">
        <div className="flex flex-col items-center opacity-50 space-y-2">
          <span className="text-[10px] uppercase tracking-widest text-gray-500">{translations.bookingWizard.instantBookingBy}</span>
          <span className="text-base font-black tracking-tighter text-navy flex items-center">
            <span className="text-mint mr-0.5">●</span> SEADENT
          </span>
        </div>
      </div>
    </Card>
  );
};

export default BookingForm;
